import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import { Container, Row, Col } from "react-bootstrap";
import Scrollspy from "react-scrollspy";
import log from "loglevel";
import { getTheme } from "lib/theme";
import { getDomainName, getFullUrl } from "lib/domain";
import { ThemeLogo } from "components/pages/auth/ThemeLogo";
import * as info from "info/documentationPageText";
import MainNav from "components/nav/MainNav";
import { decodeJwtAction } from "store/actions/actionCreators";
import { JWT } from "types";
import { getJwtReducerResponse } from "store/selectors";
import {
  Breadcrumb,
  BreadcrumbData,
  InfoTableCard,
  Page,
  PageTemplate,
} from "@avlino-inc/alenza-react";
import styles from "./DocumentationPage.module.scss";
import * as routes from "routes";
import { apiVersion } from "settings";

const DocumentationPage = ({ authed }: { authed: Promise<boolean> }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const [theme, setTheme] = useState<string | undefined>();

  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("DocumentationPage useEffect()...");
    dispatch(decodeJwtAction({}));
    setTheme(getTheme());
    log.info(`Domain Name: ${getDomainName()}`);
  }, [dispatch]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.DASHBOARD_MAIN,
        label: "Command Center",
      },
      {
        url: routes.DOCUMENTATION,
        label: "Documentation",
      },
    ],
    []
  );

  const getDropdownButtonClass = (id: string) => {
    let baseClass = "sidenav__dropdown-btn";
    if (getFullUrl().endsWith(id)) {
      baseClass += "__active";
    }
    return styles[baseClass];
  };

  const getDropdownItemClass = (id: string) => {
    let baseClass = "sidenav__dropdown-item";
    if (getFullUrl().endsWith(id)) {
      baseClass += "__active";
    }
    return styles[baseClass];
  };

  const createGeneralRolesSection = () => {
    return (
      <div style={{ paddingLeft: "15px" }}>
        <button className={getDropdownButtonClass("#general-roles")}>
          <a href="#general-roles">General Roles</a>
        </button>
        <div>
          <a
            href="#administrator-general-role"
            className={getDropdownItemClass("#administrator-general-role")}
          >
            Administrator
          </a>
          <a
            href="#member-general-role"
            className={getDropdownItemClass("#member-general-role")}
          >
            Member
          </a>
        </div>
      </div>
    );
  };

  const createAccountRolesSection = () => {
    return (
      <div style={{ paddingLeft: "15px" }}>
        <button className={getDropdownButtonClass("#account-roles")}>
          <a href="#account-roles">Account Roles</a>
        </button>
        <div>
          <a
            href="#account-owner-role"
            className={getDropdownItemClass("#account-owner-role")}
          >
            Account Owner
          </a>
          <a
            href="#account-member-role"
            className={getDropdownItemClass("#account-member-role")}
          >
            Account Member
          </a>
        </div>
      </div>
    );
  };

  const createTeamRolesSection = () => {
    return (
      <div style={{ paddingLeft: "15px" }}>
        <button className={getDropdownButtonClass("#team-roles")}>
          <a href="#team-roles">Team Roles</a>
        </button>
        <div>
          <a
            href="#team-leader-role"
            className={getDropdownItemClass("#team-leader-role")}
          >
            Team Leader
          </a>
          <a
            href="#team-member-role"
            className={getDropdownItemClass("#team-member-role")}
          >
            Team Member
          </a>
        </div>
      </div>
    );
  };

  const createRoleCategoriesSection = () => {
    return (
      <div style={{ paddingLeft: "15px" }}>
        <button className={getDropdownButtonClass("#role-categories")}>
          <a href="#role-categories">Role Categories</a>
        </button>
        <div>
          {createGeneralRolesSection()}
          {createAccountRolesSection()}
          {createTeamRolesSection()}
        </div>
      </div>
    );
  };

  const createRolesSection = () => {
    return (
      <>
        <button className={getDropdownButtonClass("#roles")}>
          <a href="#roles">Roles</a>
        </button>
        <div>
          <a
            href="#roles-overview"
            className={getDropdownItemClass("#roles-overview")}
          >
            Overview
          </a>
          {createRoleCategoriesSection()}
        </div>
      </>
    );
  };

  const createAccountsSection = () => {
    return (
      <div style={{ paddingLeft: "15px" }}>
        <button className={getDropdownButtonClass("#accounts")}>
          <a href="#accounts">Accounts</a>
        </button>
        <div>
          <a href="#analyses" className={getDropdownItemClass("#analyses")}>
            Analyses
          </a>
          <a href="#teams" className={getDropdownItemClass("#teams")}>
            Teams
          </a>
          <a
            href="#account-users"
            className={getDropdownItemClass("#account-users")}
          >
            Users (Account)
          </a>
        </div>
      </div>
    );
  };

  const createUserMangementSection = () => {
    return (
      <>
        <button className={getDropdownButtonClass("#user-management")}>
          <a href="#user-management">User Management</a>
        </button>
        <div>
          <a
            href="#user-management-overview"
            className={getDropdownItemClass("#user-management-overview")}
          >
            Overview
          </a>
          {createAccountsSection()}
        </div>
      </>
    );
  };

  return (
    <div id="DocumentationPage" style={{ height: "100%", width: "100%" }}>
      <MainNav
        first_name={jwtPayloadData && jwtPayloadData.first_name}
        last_name={jwtPayloadData && jwtPayloadData.last_name}
        username={jwtPayloadData && jwtPayloadData.username}
        email={jwtPayloadData && jwtPayloadData.email}
      />
      <PageTemplate breadcrumbs={breadcrumbItems} withStickyHeader>
        <Container className="h-100" fluid={true} style={{ minWidth: "100%" }}>
          <Row className="h-100">
            <Col md={2} className={styles["documentation__navigation"]}>
              <Scrollspy
                items={["header", "user-management", "roles"]}
                currentClassName={styles["scroll-spy"]}
                style={{ position: "fixed" }}
              >
                {createUserMangementSection()}
                {createRolesSection()}
              </Scrollspy>
            </Col>
            <Col md={6} className={styles["documentation__content"]}>
              <div style={{ height: "100%" }}>
                <section
                  id="header"
                  className={styles["documentation__content__header"]}
                >
                  <h1>Documentation</h1>
                  <h2>Version: {apiVersion}</h2>
                </section>
                <section id="user-management">
                  <h1 id="user-management">User Management</h1>
                  <br />
                  <h2 id="user-management-overview">Overview</h2>
                  {info.userManagementOverview()}
                  <br />
                  <h2 id="accounts">Accounts</h2>
                  {info.accountsDescription()}
                  <br />
                  <h3 id="analyses">Analyses</h3>
                  {info.analysesDescription()}
                  <br />
                  <h3 id="teams">Teams</h3>
                  {info.teamsDescription()}
                  <br />
                  <h3 id="account-users">Users (Account)</h3>
                  {info.accountUsersDescription()}
                  <br />
                </section>
                <section id="roles">
                  <h1 id="roles">Roles</h1>
                  <br />
                  <h2 id="roles-overview">Overview</h2>
                  {info.roleOverview()}
                  <br />
                  <h2 id="role-categories">Role Categories</h2>
                  <br />
                  <h3 id="general-roles">General Roles</h3>
                  {info.generalRoleOverview()}
                  <br />
                  <h4 id="administrator-general-role">Administrator</h4>
                  {info.administratorRoleDescription()}
                  <br />
                  <h4 id="member-general-role">Member</h4>
                  {info.memberRoleDescription()}
                  <br />
                  <h3 id="account-roles">Account Roles</h3>
                  {info.accountRoleOverview()}
                  <br />
                  <h4 id="account-owner-role">Account Owner</h4>
                  {info.accountOwnerRoleDescription()}
                  <br />
                  <h4 id="account-member-role">Account Member</h4>
                  {info.accountMemberDescription()}
                  <br />
                  <h3 id="team-roles">Team Roles</h3>
                  {info.teamRoleOverview()}
                  <br />
                  <h4 id="team-leader-role">Team Leader</h4>
                  {info.teamLeaderDescription()}
                  <br />
                  <h4 id="team-member-role">Team Member</h4>
                  {info.teamMemberDescription()}
                  <br />
                </section>
              </div>
            </Col>
            <Col md={3} />
          </Row>
        </Container>
      </PageTemplate>
    </div>
  );
};

export default DocumentationPage;
